// Owner-only admin page for the data_plate_locations knowledge base.
// Route: /owner/data-plates  (gated by Kenneth's UUID in App.js)
//
// Minimal CRUD UI by design — no search, no filter, no bulk import.
// Add features in a future session when there are 20+ entries and a real
// editing workflow is in view.

import { useEffect, useState } from 'react'
import { supabase } from '../supabase'
import CasavioLogo from './shared/CasavioLogo'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020', dark: '#1C1209',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

const ROLE_OPTIONS = ['data-plate', 'branding-panel', 'model-sticker', 'front', 'other']

const MAX_PHOTO_STEPS = 4

function emptyForm() {
  return {
    appliance_type: '',
    brand: '',
    photos: [{ role: 'data-plate', instruction: '', required: true }],
    spec_extraction_hints_csv: '',
    notes: '',
  }
}

function rowToForm(row) {
  const photos = Array.isArray(row.photos) && row.photos.length > 0
    ? row.photos.map(p => ({
        role: p.role || 'data-plate',
        instruction: p.instruction || '',
        required: p.required !== false,
      }))
    : [{ role: 'data-plate', instruction: '', required: true }]
  return {
    appliance_type: row.appliance_type || '',
    brand: row.brand || '',
    photos,
    spec_extraction_hints_csv: (row.spec_extraction_hints || []).join(', '),
    notes: row.notes || '',
  }
}

function formToPayload(form) {
  const hints = form.spec_extraction_hints_csv
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
  return {
    appliance_type: form.appliance_type.trim(),
    brand: form.brand.trim().toLowerCase(),
    photos: form.photos.map(p => ({
      role: p.role,
      instruction: p.instruction.trim(),
      required: !!p.required,
    })),
    spec_extraction_hints: hints,
    notes: form.notes.trim() || null,
    last_updated: new Date().toISOString(),
  }
}

function validate(form) {
  if (!form.appliance_type.trim()) return 'Appliance type is required.'
  if (!form.brand.trim()) return 'Brand is required.'
  if (form.photos.length === 0) return 'At least one photo step is required.'
  for (let i = 0; i < form.photos.length; i++) {
    if (!form.photos[i].instruction.trim()) return `Photo step ${i + 1} needs an instruction.`
  }
  return null
}

export default function OwnerDataPlates({ session }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyForm())
  const [saving, setSaving] = useState(false)
  const [formErr, setFormErr] = useState(null)

  useEffect(() => {
    window.history.pushState({}, '', '/owner/data-plates')
    return () => window.history.pushState({}, '', '/')
  }, [])

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    setErr(null)
    const { data, error } = await supabase
      .from('data_plate_locations')
      .select('id, appliance_type, brand, photos, spec_extraction_hints, notes, confidence, last_updated, created_at')
      .is('deleted_at', null)
      .order('appliance_type', { ascending: true })
      .order('brand', { ascending: true })
    if (error) setErr(error.message)
    else setRows(data || [])
    setLoading(false)
  }

  function openNew() {
    setEditingId(null)
    setForm(emptyForm())
    setFormErr(null)
    setModalOpen(true)
  }

  function openEdit(row) {
    setEditingId(row.id)
    setForm(rowToForm(row))
    setFormErr(null)
    setModalOpen(true)
  }

  async function softDelete(row) {
    if (!window.confirm(`Soft-delete the ${row.brand} / ${row.appliance_type} entry? It can be restored by clearing deleted_at.`)) return
    const { error } = await supabase
      .from('data_plate_locations')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', row.id)
    if (error) {
      window.alert(`Delete failed: ${error.message}`)
      return
    }
    await load()
  }

  async function save() {
    const v = validate(form)
    if (v) { setFormErr(v); return }
    setFormErr(null)
    setSaving(true)
    const payload = formToPayload(form)
    let error
    if (editingId) {
      ;({ error } = await supabase.from('data_plate_locations').update(payload).eq('id', editingId))
    } else {
      ;({ error } = await supabase.from('data_plate_locations').insert(payload))
    }
    setSaving(false)
    if (error) {
      setFormErr(error.message)
      return
    }
    setModalOpen(false)
    await load()
  }

  function updateField(k, v) { setForm(prev => ({ ...prev, [k]: v })) }
  function updatePhoto(idx, key, val) {
    setForm(prev => ({
      ...prev,
      photos: prev.photos.map((p, i) => i === idx ? { ...p, [key]: val } : p),
    }))
  }
  function addPhoto() {
    if (form.photos.length >= MAX_PHOTO_STEPS) return
    setForm(prev => ({ ...prev, photos: [...prev.photos, { role: 'data-plate', instruction: '', required: true }] }))
  }
  function removePhoto(idx) {
    if (form.photos.length <= 1) return
    setForm(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }))
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: fs }}>
      {/* Header bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: C.dark, borderBottom: `2px solid ${C.amber}`,
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '0 20px', height: '50px',
      }}>
        <CasavioLogo size={15} color={C.amber} style={{ marginRight: '12px', flexShrink: 0 }} />
        <a href="/owner" style={{ color: '#CCC', textDecoration: 'none', fontSize: '12px', padding: '3px 10px', borderRadius: '6px', background: '#2A1E12', border: '1px solid #3D2C1A', fontFamily: fs }}>
          ← Founder Command Center
        </a>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.muted, fontSize: '11px', fontFamily: fs }}>Data Plate Knowledge Base · Kenneth only</span>
      </div>

      <div style={{ maxWidth: '980px', margin: '0 auto', padding: '28px 20px' }}>
        <h1 style={{ fontFamily: f, fontSize: '26px', color: C.brown, margin: '0 0 4px' }}>Data Plate Locations</h1>
        <p style={{ color: C.muted, fontSize: '12px', margin: '0 0 24px', fontFamily: fs }}>
          Brand+type entries powering the two-photo scan flow. Each row teaches Casavio where the data plate is and what badges/series/features to extract.
        </p>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', color: C.muted }}>
            {loading ? 'Loading…' : `${rows.length} live entr${rows.length === 1 ? 'y' : 'ies'}`}
          </div>
          <button
            onClick={openNew}
            style={{
              background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
              color: C.card, border: 'none', borderRadius: '8px',
              padding: '8px 14px', fontSize: '13px', fontFamily: f, fontWeight: 700,
              letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer',
            }}
          >
            + Add Entry
          </button>
        </div>

        {err && (
          <div style={{ background: '#F6E5DF', color: C.danger, border: `1px solid ${C.danger}55`, borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' }}>
            {err}
          </div>
        )}

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1.2fr 0.8fr 0.8fr', background: '#FAF6EE', padding: '10px 14px', fontSize: '11px', letterSpacing: '1.2px', color: C.muted, textTransform: 'uppercase', fontFamily: fs }}>
            <div>Appliance type</div>
            <div>Brand</div>
            <div>Last updated</div>
            <div style={{ textAlign: 'right' }}>Edit</div>
            <div style={{ textAlign: 'right' }}>Delete</div>
          </div>
          {loading ? (
            <div style={{ padding: '16px', color: C.muted, fontSize: '13px' }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: '20px', color: C.muted, fontSize: '13px' }}>
              No entries yet. Tap “+ Add Entry” to create the first one.
            </div>
          ) : rows.map(row => (
            <div key={row.id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1.2fr 0.8fr 0.8fr', padding: '12px 14px', borderTop: `1px solid ${C.border}`, alignItems: 'center', fontSize: '13px', color: C.brown }}>
              <div style={{ fontFamily: 'Consolas, monospace' }}>{row.appliance_type}</div>
              <div>{row.brand}</div>
              <div style={{ color: C.muted, fontSize: '12px' }}>
                {row.last_updated ? new Date(row.last_updated).toLocaleString() : '—'}
              </div>
              <div style={{ textAlign: 'right' }}>
                <button onClick={() => openEdit(row)} style={{ background: 'transparent', border: `1px solid ${C.amber}`, borderRadius: '6px', color: C.amber, padding: '4px 10px', fontSize: '12px', cursor: 'pointer', fontFamily: fs }}>
                  Edit
                </button>
              </div>
              <div style={{ textAlign: 'right' }}>
                <button onClick={() => softDelete(row)} style={{ background: 'transparent', border: `1px solid ${C.danger}55`, borderRadius: '6px', color: C.danger, padding: '4px 10px', fontSize: '12px', cursor: 'pointer', fontFamily: fs }}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '14px', fontSize: '11px', color: C.muted, lineHeight: 1.5 }}>
          Delete is a soft delete — sets <code>deleted_at</code>. To restore, clear that column in Supabase Studio.
        </div>
      </div>

      {modalOpen && (
        <div onClick={() => !saving && setModalOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px', zIndex: 200,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: '14px',
            padding: '24px', maxWidth: '620px', width: '100%', maxHeight: '85vh',
            overflowY: 'auto', boxShadow: '0 12px 40px rgba(44,29,20,0.25)',
          }}>
            <h2 style={{ fontFamily: f, fontSize: '18px', color: C.brown, margin: '0 0 16px' }}>
              {editingId ? 'Edit data plate entry' : 'Add data plate entry'}
            </h2>

            <Field label="Appliance type" hint="e.g. washer-top-load, refrigerator-side-by-side, dryer">
              <input
                type="text"
                value={form.appliance_type}
                onChange={(e) => updateField('appliance_type', e.target.value)}
                style={S.input}
                placeholder="washer-top-load"
              />
            </Field>

            <Field label="Brand" hint="lowercase, e.g. kenmore, lg, whirlpool (saved as lowercase automatically)">
              <input
                type="text"
                value={form.brand}
                onChange={(e) => updateField('brand', e.target.value)}
                style={S.input}
                placeholder="kenmore"
              />
            </Field>

            <Field label="Photo steps" hint={`${form.photos.length} of ${MAX_PHOTO_STEPS} — at least one is required`}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {form.photos.map((p, i) => (
                  <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: '10px', padding: '10px 12px', background: C.bg }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '11px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase' }}>Step {i + 1}</span>
                      {form.photos.length > 1 && (
                        <button type="button" onClick={() => removePhoto(i)} style={{ background: 'transparent', border: 'none', color: C.danger, fontSize: '12px', cursor: 'pointer' }}>
                          Remove
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                      <select value={p.role} onChange={(e) => updatePhoto(i, 'role', e.target.value)} style={{ ...S.input, flex: '0 0 180px' }}>
                        {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.brown }}>
                        <input
                          type="checkbox"
                          checked={p.required}
                          onChange={(e) => updatePhoto(i, 'required', e.target.checked)}
                        />
                        Required
                      </label>
                    </div>
                    <textarea
                      value={p.instruction}
                      onChange={(e) => updatePhoto(i, 'instruction', e.target.value)}
                      style={{ ...S.input, minHeight: '60px', resize: 'vertical' }}
                      placeholder="e.g. Open the door — data plate is on the lower-left frame."
                    />
                  </div>
                ))}
                {form.photos.length < MAX_PHOTO_STEPS && (
                  <button type="button" onClick={addPhoto} style={{ alignSelf: 'flex-start', background: 'transparent', border: `1px dashed ${C.amber}`, color: C.amber, borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', fontFamily: fs }}>
                    + Add photo step
                  </button>
                )}
              </div>
            </Field>

            <Field label="Spec extraction hints" hint="comma-separated badges / series / model patterns the AI should look for">
              <input
                type="text"
                value={form.spec_extraction_hints_csv}
                onChange={(e) => updateField('spec_extraction_hints_csv', e.target.value)}
                style={S.input}
                placeholder="Series 600S, Triple Action Technology, HE"
              />
            </Field>

            <Field label="Notes" hint="internal context: model year ranges, edge cases, anything Future-Kenneth needs">
              <textarea
                value={form.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                style={{ ...S.input, minHeight: '70px', resize: 'vertical' }}
                placeholder="Top-load Kenmore washers from 2010-2020 typically have plates on the right lid rim…"
              />
            </Field>

            {formErr && (
              <div style={{ background: '#F6E5DF', color: C.danger, border: `1px solid ${C.danger}55`, borderRadius: '8px', padding: '8px 12px', marginTop: '6px', fontSize: '12px' }}>
                {formErr}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={save} disabled={saving} style={{
                flex: 1, background: saving ? C.border : `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
                color: saving ? C.muted : C.card, border: 'none', borderRadius: '10px',
                padding: '11px', fontSize: '13px', fontFamily: f, fontWeight: 700,
                letterSpacing: '1px', textTransform: 'uppercase', cursor: saving ? 'default' : 'pointer',
              }}>
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create entry'}
              </button>
              <button onClick={() => setModalOpen(false)} disabled={saving} style={{
                background: 'transparent', color: C.muted, border: `1px solid ${C.border}`,
                borderRadius: '10px', padding: '11px 16px', fontSize: '13px', fontFamily: fs, cursor: 'pointer',
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '11px', color: C.muted, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: '11px', color: C.muted, marginTop: '4px' }}>{hint}</div>}
    </div>
  )
}

const S = {
  input: {
    width: '100%', background: C.card, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '8px 12px', fontSize: '13px',
    fontFamily: fs, color: C.brown, boxSizing: 'border-box', outline: 'none',
  },
}
